/**
 * Utility functions untuk manage wishlist di localStorage
 */

const WISHLIST_STORAGE_KEY = "basestore_wishlist";

function notifyWishlistUpdated() {
  if (typeof window === "undefined") return;
  try {
    window.dispatchEvent(new Event("basestore:wishlist-updated"));
  } catch {
    // ignore
  }
}

/**
 * Ambil semua items dari wishlist
 */
export function getWishlistItems() {
  if (typeof window === "undefined") return [];
  try {
    const items = localStorage.getItem(WISHLIST_STORAGE_KEY);
    return items ? JSON.parse(items) : [];
  } catch (error) {
    console.error("Error reading wishlist from localStorage:", error);
    return [];
  }
}

/**
 * Simpan items ke wishlist
 */
export function saveWishlistItems(items) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
    notifyWishlistUpdated();
  } catch (error) {
    console.error("Error saving wishlist to localStorage:", error);
  }
}

/**
 * Tambah item ke wishlist
 * - Dari product list/category: product-level saja → variantId: null, variantData: null
 * - Dari product detail: per variant → isi variantId & variantData
 * @param {Object} item - Item yang akan ditambahkan
 * @param {string|number} item.productId - ID/slug produk
 * @param {string|number|null} [item.variantId=null] - ID variant (opsional)
 * @param {Object} item.productData - Data produk (harus ada uuid untuk sync API)
 * @param {Object|null} [item.variantData=null] - Data variant (opsional)
 */
export function addToWishlist(item) {
  const { productId, variantId, productData, variantData } = item;
  const wishlistItems = getWishlistItems();

  // Cek apakah item dengan variant yang sama sudah ada di wishlist
  // Unique key: productId + variantId (null dianggap sebagai variantId khusus)
  const existingItem = wishlistItems.find(
    (wishlistItem) =>
      wishlistItem.productId === productId &&
      wishlistItem.variantId === (variantId || null)
  );

  if (!existingItem) {
    // Tambah item baru dengan variant
    wishlistItems.push({
      productId,
      variantId: variantId || null,
      productData: productData || {},
      variantData: variantData || null,
      addedAt: new Date().toISOString(),
    });
    saveWishlistItems(wishlistItems);
  }

  return wishlistItems;
}

/**
 * Hapus item dari wishlist
 * @param {string|number} productId - ID produk
 * @param {string|number|null} [variantId=null] - ID variant (opsional; null = product-level)
 */
export function removeFromWishlist(productId, variantId = null) {
  const wishlistItems = getWishlistItems();
  const filteredItems = wishlistItems.filter(
    (item) =>
      !(item.productId === productId && item.variantId === (variantId || null))
  );
  saveWishlistItems(filteredItems);
  return filteredItems;
}

/**
 * Hapus semua wishlist berdasarkan product uuid (hapus per product)
 * @param {string} productUuid
 */
export function removeWishlistByProductUuid(productUuid) {
  if (!productUuid) return getWishlistItems();
  const wishlistItems = getWishlistItems();
  const filteredItems = wishlistItems.filter((item) => {
    const puuid =
      item?.productData?.uuid || item?.productData?.product_uuid || item?.productUuid || null;
    return puuid !== productUuid;
  });
  saveWishlistItems(filteredItems);
  return filteredItems;
}

/**
 * Cek apakah produk/variant sudah ada di wishlist
 * @param {string|number} productId - ID produk
 * @param {string|number|null} [variantId=null] - ID variant (opsional; null = cek product-level)
 */
export function isInWishlist(productId, variantId = null) {
  const wishlistItems = getWishlistItems();
  return wishlistItems.some(
    (item) =>
      item.productId === productId && item.variantId === (variantId || null)
  );
}

/**
 * Hapus semua items dari wishlist
 */
export function clearWishlist() {
  saveWishlistItems([]);
  return [];
}

/**
 * Hitung total items di wishlist
 */
export function getWishlistTotalItems() {
  const wishlistItems = getWishlistItems();
  return wishlistItems.length;
}

